// Dictation session controller.
//
// A module singleton rather than React context, for a structural reason: the
// speech stream is owned by WakeWordDetector (mounted in ProviderDashboard) and
// the note being dictated lives in ClientProfile, several trees away. Threading
// context between them would mean touching both. `clinicVoice` in
// useVoiceCommands is already a module singleton for the same reason.
//
// The browser realistically supports ONE SpeechRecognition instance. Starting a
// second one for dictation fights the wake-word listener and both drop audio.
// So dictation does not open its own stream — WakeWordDetector checks
// `isActive()` and routes speech here instead of parsing it as commands.

export type DictationStatus = 'idle' | 'listening' | 'processing' | 'error';

export interface DictationState {
  status: DictationStatus;
  /** Everything captured this session, joined. */
  transcript: string;
  segments: string[];
  startedAt: number | null;
  /** Note template the result will be structured into. */
  template: string;
  /** Where the finished note should land. */
  target: DictationTarget | null;
  error: string | null;
}

export interface DictationTarget {
  patientId?: string;
  noteId?: string;
  /** Free-form label shown in the UI ("Progress note — J.D."). */
  label?: string;
}

export interface StartDictationOptions {
  template?: string;
  target?: DictationTarget;
}

type Listener = (state: DictationState) => void;

/**
 * Phrases that end dictation. Matched loosely because speech recognition
 * punctuates and capitalizes unpredictably.
 *
 * Deliberately NOT included: bare "stop" and bare "clinic". Both appear in
 * ordinary clinical speech ("we agreed she would stop the medication"), and the
 * wake-word list treats "clinic" alone as an activation. Ending a note by
 * accident mid-sentence is worse than needing an extra word to end it.
 */
const STOP_PATTERNS = [
  /\bstop\s+dictation\b/i,
  /\bstop\s+dictating\b/i,
  /\bend\s+dictation\b/i,
  /\bend\s+dictating\b/i,
  /\bfinish\s+dictation\b/i,
  /\bdictation\s+off\b/i,
  /\bstop\s+the\s+dictation\b/i,
];

const CANCEL_PATTERNS = [
  /\bcancel\s+dictation\b/i,
  /\bdiscard\s+dictation\b/i,
  /\bscrap\s+(the\s+)?dictation\b/i,
];

const INITIAL_STATE: DictationState = {
  status: 'idle',
  transcript: '',
  segments: [],
  startedAt: null,
  template: 'BIRP',
  target: null,
  error: null,
};

class DictationControllerImpl {
  private state: DictationState = { ...INITIAL_STATE };
  private listeners = new Set<Listener>();
  private processor: ((transcript: string) => void) | null = null;

  getState(): DictationState {
    return this.state;
  }

  /** Synchronous check — WakeWordDetector calls this on every speech result. */
  isActive(): boolean {
    return this.state.status === 'listening';
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(patch: Partial<DictationState>): void {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((listener) => listener(this.state));
  }

  start(options: StartDictationOptions = {}): void {
    if (this.state.status === 'listening') return;

    this.emit({
      status: 'listening',
      transcript: '',
      segments: [],
      startedAt: Date.now(),
      template: options.template || 'BIRP',
      target: options.target || null,
      error: null,
    });
  }

  /** Add one recognized utterance. Raw text — casing and wording are preserved. */
  appendSegment(text: string): void {
    const trimmed = (text || '').trim();
    if (!trimmed || this.state.status !== 'listening') return;

    const segments = [...this.state.segments, trimmed];
    this.emit({ segments, transcript: segments.join(' ') });
  }

  /**
   * Register who turns a finished transcript into a structured note.
   *
   * Needed because "stop dictation" is spoken, and arrives through the speech
   * listener — which has no access to the React hook that owns the API call.
   * One processor at a time, so a voice stop and a button stop take the exact
   * same path and cannot double-submit.
   */
  setProcessor(processor: ((transcript: string) => void) | null): void {
    this.processor = processor;
  }

  /** Ends capture and hands the transcript to the registered processor. */
  stop(): string {
    if (this.state.status !== 'listening') return this.state.transcript;

    const { transcript } = this.state;
    const hasContent = transcript.trim().length > 0;
    this.emit({ status: hasContent ? 'processing' : 'idle' });

    if (hasContent && this.processor) this.processor(transcript);
    return transcript;
  }

  /** Abandon the session and throw the transcript away. */
  cancel(): void {
    this.emit({ ...INITIAL_STATE });
  }

  finishProcessing(): void {
    this.emit({ ...INITIAL_STATE });
  }

  failProcessing(error: string): void {
    this.emit({ status: 'error', error });
  }

  reset(): void {
    this.emit({ ...INITIAL_STATE });
  }
}

export const DictationController = new DictationControllerImpl();

/** Does this utterance end the session? */
export function isStopPhrase(text: string): boolean {
  return STOP_PATTERNS.some((pattern) => pattern.test(text));
}

export function isCancelPhrase(text: string): boolean {
  return CANCEL_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * Strip control phrases out of an utterance.
 * Recognition usually delivers "...increasing to weekly stop dictation" as one
 * result, so the command has to come out of the note text before it is kept.
 */
export function stripControlPhrases(text: string): string {
  let cleaned = text;
  [...STOP_PATTERNS, ...CANCEL_PATTERNS].forEach((pattern) => {
    cleaned = cleaned.replace(pattern, '');
  });
  return cleaned.replace(/\bhey\s+clinic\b/gi, '').replace(/\s+/g, ' ').trim();
}
